import { type FormEvent, lazy, Suspense, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/card";
import { EmptyState, ErrorBanner, Loading, ResendWarning } from "@/components/ui/feedback";
import { Select, TextInput } from "@/components/ui/field";
import {
  useAccounts,
  useCategories,
  useCreateTransaction,
  useCreateTransfer,
  useDeleteTransaction,
  useTransaction,
  useUpdateTransaction,
} from "../../api/finance";
import { useMe } from "../../api/session";
import type { Category, CategoryKind, Transaction, TransactionType } from "../../api/types";
import { MoneyInput } from "../../components/MoneyInput";
import { useCan, useWorkspace } from "../../layout/workspace-context";
import { todayCivil } from "../../lib/dates";
import { useIdempotencyKey } from "../../lib/idempotency";
import { parseMoneyInput, toInputValue } from "../../lib/money";

// O comprovante (#37) só aparece no detalhe de um lançamento existente (D14): fora do JS inicial.
const AttachmentSection = lazy(() => import("../attachments/AttachmentSection"));

const KIND_LABEL: Record<TransactionType, string> = {
  expense: "Despesa",
  income: "Receita",
  transfer: "Transferência",
};

function categoryOptions(categories: Category[] | undefined, kind: CategoryKind) {
  const ofKind = (categories ?? []).filter((c) => c.kind === kind && !c.archivedAt);
  const roots = ofKind.filter((c) => !c.parentId);
  return roots.flatMap((r) => [r, ...ofKind.filter((c) => c.parentId === r.id)]);
}

export function TransactionFormPage() {
  const ws = useWorkspace();
  const { id } = useParams();
  const editing = useTransaction(ws.id, id);
  if (id && editing.isPending) return <Loading />;
  if (id && editing.isError) return <ErrorBanner error={editing.error} />;
  return <TransactionForm key={id ?? "new"} existing={editing.data} />;
}

function TransactionForm({ existing }: { existing?: Transaction }) {
  const ws = useWorkspace();
  const navigate = useNavigate();
  const canWrite = useCan("transaction:write");
  const accounts = useAccounts(ws.id);
  const categories = useCategories(ws.id);
  const create = useCreateTransaction(ws.id);
  const transfer = useCreateTransfer(ws.id);
  const update = useUpdateTransaction(ws.id);
  const remove = useDeleteTransaction(ws.id);
  const attachmentsEnabled = useMe().data?.attachmentsEnabled ?? false;

  const [kind, setKind] = useState<TransactionType>(existing?.type ?? "expense");
  const [amount, setAmount] = useState(existing ? toInputValue(existing.amountMinor) : "");
  const [date, setDate] = useState(existing?.date ?? todayCivil(ws.timezone));
  const [accountId, setAccountId] = useState(existing?.accountId ?? "");
  const [toAccountId, setToAccountId] = useState("");
  const [categoryId, setCategoryId] = useState(existing?.categoryId ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [pending, setPending] = useState(existing?.status === "pending");
  const [tags, setTags] = useState(existing?.tags.join(", ") ?? "");
  const [notes, setNotes] = useState(existing?.notes ?? "");
  const [invalid, setInvalid] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmResend, setConfirmResend] = useState(false);
  // Só a criação usa a chave (edição e exclusão já são idempotentes).
  const idempotency = useIdempotencyKey({
    kind,
    amount,
    date,
    accountId,
    toAccountId,
    categoryId,
    description,
    pending,
    tags,
    notes,
  });

  const options = kind === "transfer" ? [] : categoryOptions(categories.data, kind);

  // Padrões quando os dados chegam: primeira conta e primeira categoria do tipo.
  useEffect(() => {
    if (!accountId && accounts.data?.[0]) setAccountId(accounts.data[0].id);
  }, [accounts.data, accountId]);
  useEffect(() => {
    if (kind !== "transfer" && !options.some((o) => o.id === categoryId) && options[0])
      setCategoryId(options[0].id);
  }, [kind, options, categoryId]);

  if (accounts.isPending) return <Loading />;
  if (accounts.data?.length === 0) {
    return (
      <EmptyState title="Crie uma conta antes de lançar.">
        <Link to={`/w/${ws.id}/accounts`} className="text-link underline">
          Ir para Contas
        </Link>
      </EmptyState>
    );
  }

  const backToList = () => navigate(`/w/${ws.id}/transactions`);
  const mutationError = create.error ?? transfer.error ?? update.error ?? remove.error;
  const busy = create.isPending || transfer.isPending || update.isPending || remove.isPending;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const amountMinor = parseMoneyInput(amount);
    if (amountMinor === null) {
      setInvalid("Informe um valor válido, ex.: 12,34");
      return;
    }
    setInvalid(null);
    // P1: a resposta anterior se perdeu e o conteúdo mudou; avisa antes de enviar um pedido novo.
    if (!existing && idempotency.changedAfterFailure) {
      setConfirmResend(true);
      return;
    }
    await save(amountMinor, idempotency.key);
  }

  async function save(amountMinor: number, idempotencyKey: string) {
    // O que este envio leva: se a resposta se perder, a falha é dele, mesmo que o formulário mude no meio.
    const sent = { key: idempotencyKey, fingerprint: idempotency.fingerprint };
    const status = pending ? "pending" : "cleared";
    const tagList = tags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    const optional = {
      ...(tagList.length ? { tags: tagList } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };

    if (existing) {
      const body =
        existing.type === "transfer"
          ? { amountMinor, date, description, status }
          : {
              amountMinor,
              date,
              description,
              status,
              accountId,
              categoryId,
              tags: tagList,
              notes: notes.trim() || null,
            };
      await update.mutateAsync({ id: existing.id, ...body });
    } else {
      try {
        if (kind === "transfer") {
          await transfer.mutateAsync({
            fromAccountId: accountId,
            toAccountId,
            amountMinor,
            date,
            description,
            status,
            ...optional,
            idempotencyKey,
          });
        } else {
          await create.mutateAsync({
            type: kind,
            amountMinor,
            date,
            accountId,
            categoryId,
            description,
            status,
            ...optional,
            idempotencyKey,
          });
        }
      } catch (error) {
        if (error instanceof ApiError && error.code === "network_error") idempotency.markFailure(sent);
        throw error;
      }
      idempotency.rotate();
    }
    backToList();
  }

  function confirmSave() {
    setConfirmResend(false);
    const amountMinor = parseMoneyInput(amount);
    if (amountMinor === null) {
      setInvalid("Informe um valor válido, ex.: 12,34");
      return;
    }
    // Se o conteúdo voltou ao da falha, a chave dela é reaproveitada (não duplica); senão, chave nova.
    const key = idempotency.changedAfterFailure ? idempotency.rotate() : idempotency.key;
    void save(amountMinor, key).catch(() => undefined);
  }

  async function doDelete() {
    if (!existing) return;
    await remove.mutateAsync(existing.id);
    backToList();
  }

  return (
    <>
      <PageHeader title={existing ? "Editar lançamento" : "Novo lançamento"} />
      <form
        onSubmit={(e) => void submit(e).catch(() => undefined)}
        className="flex max-w-xl flex-col gap-4 rounded-xl border border-border bg-surface p-4"
      >
        <fieldset className="flex gap-2" disabled={Boolean(existing)}>
          <legend className="sr-only">Tipo</legend>
          {(["expense", "income", "transfer"] as const).map((k) => (
            <label
              key={k}
              className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm"
            >
              <input type="radio" name="kind" value={k} checked={kind === k} onChange={() => setKind(k)} />
              {KIND_LABEL[k]}
            </label>
          ))}
        </fieldset>

        <MoneyInput label="Valor" required value={amount} onChange={(e) => setAmount(e.target.value)} />
        {invalid && <p className="text-sm text-expense">{invalid}</p>}
        <TextInput label="Data" type="date" required value={date} onChange={(e) => setDate(e.target.value)} />

        {kind === "transfer" ? (
          !existing && (
            <>
              <Select label="De" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                {accounts.data?.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </Select>
              <Select
                label="Para"
                required
                value={toAccountId}
                onChange={(e) => setToAccountId(e.target.value)}
              >
                <option value="">Escolha a conta</option>
                {accounts.data
                  ?.filter((a) => a.id !== accountId)
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
              </Select>
            </>
          )
        ) : (
          <>
            <Select label="Conta" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              {accounts.data?.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
            <Select label="Categoria" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              {options.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.parentId ? `— ${c.name}` : c.name}
                </option>
              ))}
            </Select>
          </>
        )}

        <TextInput
          label="Descrição"
          required
          maxLength={200}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={pending} onChange={(e) => setPending(e.target.checked)} />
          Pendente (ainda não pago/recebido)
        </label>
        {existing?.type !== "transfer" && (
          <>
            <TextInput
              label="Tags"
              hint="Separe por vírgula"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
            />
            <TextInput label="Notas" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </>
        )}

        <ErrorBanner error={mutationError} saving={!existing && mutationError !== remove.error} />
        {confirmResend && <ResendWarning onConfirm={confirmSave} onCancel={() => setConfirmResend(false)} />}
        <div className="flex flex-wrap justify-between gap-2">
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || !canWrite}>
              Salvar
            </Button>
            <Button variant="secondary" onClick={backToList}>
              Cancelar
            </Button>
          </div>
          {existing &&
            canWrite &&
            (confirmDelete ? (
              <Button variant="danger" disabled={busy} onClick={() => void doDelete().catch(() => undefined)}>
                Confirmar exclusão
              </Button>
            ) : (
              <Button variant="ghost" onClick={() => setConfirmDelete(true)}>
                Excluir
              </Button>
            ))}
        </div>
      </form>
      {existing && attachmentsEnabled && (
        <Suspense fallback={null}>
          <AttachmentSection tx={existing} />
        </Suspense>
      )}
    </>
  );
}
